import React from "react";
import { Button, Dialog } from "@/components/ui";
import { CheckCircle2, AlertCircle, Sparkles, Languages, ShieldCheck, HelpCircle } from "lucide-react";

interface FormPersonalizationHelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FormPersonalizationHelpDialog({ open, onOpenChange }: FormPersonalizationHelpDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="xl">
      <Dialog.Header>
        <Dialog.Title className="flex items-center gap-2 text-xl font-bold font-sans">
          <img alt={"SocialCoach AI"} src="https://cdn.socialcoach.io/socoai_mono_positive.svg" className="h-[16px]" />
          <span>Personalization & Translation</span>
        </Dialog.Title>
        <Dialog.Description className="text-slate-500 text-sm">
          Learn how our independent AI engines personalize form greetings and seamlessly translate your survey
          experiences.
        </Dialog.Description>
      </Dialog.Header>

      <Dialog.Content className="max-h-[70vh] overflow-y-auto space-y-6 py-4">
        {/* Dual Engines Explanation */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-slate-50 border border-slate-200/50 p-4 rounded-xl leading-normal text-sm text-slate-600 space-y-2">
            <div className="flex items-center gap-2 font-semibold text-slate-800 text-base">
              <Languages className="size-4.5 text-indigo-600" />
              <span>1. Translation Engine</span>
            </div>
            <p>
              Our dedicated <strong>Translation</strong> service translates the entire form experience dynamically for
              users preferring other languages. It handles form instructions, questions, multiple-choice options, and
              CTA actions completely independently from personalization to ensure reliable, high-fidelity localized
              surveys.
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200/50 p-4 rounded-xl leading-normal text-sm text-slate-600 space-y-2">
            <div className="flex items-center gap-2 font-semibold text-slate-800 text-base">
              <Sparkles className="size-4.5 text-primary" />
              <span>2. AI Personalization Engine</span>
            </div>
            <p>
              SocialCoach's <strong>AI Personalization</strong> service automatically customizes form intros, question
              framing, and greetings based on details like the <strong>Client (Fan)</strong>, the{" "}
              <strong>Officer (Player)</strong>, and transaction-specific context (such as{" "}
              <strong>Loan or Closing Details</strong>). This results in far higher survey engagement and completion
              rates.
            </p>
          </div>
        </div>

        {/* side by side comparisons */}
        <div className="space-y-4">
          <span className="text-lg font-semibold text-slate-500 flex items-center gap-1.5 select-none">
            <CheckCircle2 className="size-4 text-emerald-500" />
            Feature Highlights & Examples
          </span>

          <div className="grid grid-cols-1 gap-4">
            {/* Example 1 - Personalization */}
            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-bold text-slate-700 flex justify-between select-none">
                <span className="flex items-center gap-1.5">
                  <Sparkles className="size-4 text-primary" />
                  AI Personalization: Greeting & Introductory Text
                </span>
                <span className="text-xs text-muted-foreground font-medium uppercase font-mono">
                  Sarah (Client) & John (Officer)
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200 text-sm">
                <div className="p-3 bg-white space-y-1">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider select-none">
                    Standard (Off)
                  </div>
                  <p className="text-slate-600">
                    "Thank you for your business. We are committed to providing the best possible experience. Please
                    take a moment to share your feedback about your recent transaction."
                  </p>
                </div>
                <div className="p-3 bg-emerald-50/20 space-y-1">
                  <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider select-none">
                    Personalized (On)
                  </div>
                  <p className="text-slate-700 font-medium">
                    "Hi <span className="text-emerald-700 font-semibold">Sarah</span>, thank you so much for working
                    with <span className="text-emerald-700 font-semibold">John</span> on your recent mortgage! We are
                    committed to providing the best possible experience. Please take a moment to let us know how John
                    did."
                  </p>
                </div>
              </div>
            </div>

            {/* Example 2 - Personalization */}
            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-bold text-slate-700 flex justify-between select-none">
                <span className="flex items-center gap-1.5">
                  <Sparkles className="size-4 text-primary" />
                  AI Personalization: Context & Loan Details
                </span>
                <span className="text-xs text-muted-foreground font-medium uppercase font-mono">VA Loan Purchase</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200 text-sm">
                <div className="p-3 bg-white space-y-1">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider select-none">
                    Standard (Off)
                  </div>
                  <p className="text-slate-600">"Did our team explain the loan requirements and paperwork clearly?"</p>
                </div>
                <div className="p-3 bg-emerald-50/20 space-y-1">
                  <div className="text-xs font-bold text-emerald-600 uppercase tracking-wider select-none">
                    Personalized (On)
                  </div>
                  <p className="text-slate-700 font-medium">
                    "Did <span className="text-emerald-700 font-semibold">John</span> explain the{" "}
                    <span className="text-emerald-700 font-semibold">VA loan</span> requirements and paperwork clearly
                    to you?"
                  </p>
                </div>
              </div>
            </div>

            {/* Example 3 - Translation */}
            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
              <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-sm font-bold text-slate-700 flex justify-between select-none">
                <span className="flex items-center gap-1.5">
                  <Languages className="size-4 text-indigo-600" />
                  Multilingual Translation (Spanish)
                </span>
                <span className="text-xs text-muted-foreground font-medium uppercase font-mono flex items-center gap-1">
                  Sarah prefers Spanish
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-slate-200 text-sm">
                <div className="p-3 bg-white space-y-1">
                  <div className="text-xs font-bold text-slate-400 uppercase tracking-wider select-none">
                    Standard English
                  </div>
                  <p className="text-slate-600">
                    "Would you refer your loan officer to friends and family in the future?"
                  </p>
                </div>
                <div className="p-3 bg-indigo-50/10 space-y-1">
                  <div className="text-xs font-bold text-indigo-600 uppercase tracking-wider select-none">
                    Translated Spanish
                  </div>
                  <p className="text-slate-700 font-medium italic">
                    "¿Recomendarías a <span className="text-indigo-700 font-semibold">John</span> a tus amigos y
                    familiares en el futuro?"
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Structural Integrity Disclaimer */}
        <div className="flex gap-3 p-4 bg-blue-50/50 border border-blue-100 rounded-xl text-sm text-blue-800 leading-normal select-none">
          <AlertCircle className="size-5 shrink-0 text-blue-500 mt-0.5" />
          <div>
            <span className="text-base font-bold block text-blue-900 mb-0.5">Your Form Structure Stays the Same</span>
            Neither personalization nor translation will ever modify the underlying layout of your forms. They customize
            the <strong>wording</strong> and language of your text, but they{" "}
            <strong>never change the type of questions, ratings, or choices</strong> you have created.
          </div>
        </div>

        {/* FAQs */}
        <div className="space-y-4">
          <span className="text-lg font-semibold text-slate-500 flex items-center gap-1.5 select-none">
            <HelpCircle className="size-4 text-slate-400" />
            Frequently Asked Questions
          </span>

          <div className="grid grid-cols-1 gap-6 text-sm leading-normal text-slate-600">
            <div className="space-y-1">
              <span className="text-base font-bold text-slate-800 flex items-center gap-1">
                <ShieldCheck className="size-4 text-emerald-500 shrink-0" />
                How are the personalization and translation engines separate?
              </span>
              <p>
                To provide maximum stability, security, and accuracy, SocialCoach runs two completely independent AI
                services on our backend. The translation service is optimized solely for localization (perfectly mapping
                questions and options into other languages like Spanish). The personalization service is optimized
                solely for tailoring greetings, tone, and transaction context (like VA loan purchase information). This
                ensures high-fidelity results for each action.
              </p>
            </div>

            <div className="space-y-1">
              <span className="text-base font-bold text-slate-800 flex items-center gap-1">
                <ShieldCheck className="size-4 text-emerald-500 shrink-0" />
                Is this compliance-friendly?
              </span>
              <p>
                Yes, absolutely. Under the hood, we configure our secure models with strict safety boundaries. The
                translation engine retains the exact meaning and regulatory intent of your questions, while the
                personalization engine is strictly prohibited from altering compliance phrasing, legal disclosures, or
                proposing unapproved incentives.
              </p>
            </div>

            <div className="space-y-1">
              <span className="text-base font-bold text-slate-800 flex items-center gap-1">
                <ShieldCheck className="size-4 text-emerald-500 shrink-0" />
                How are private details handled?
              </span>
              <p>
                Since feedback forms are shared via public links, the personalization service features strict built-in
                privacy guards. It automatically strips out or generalizes highly sensitive details such as street
                addresses, specific loan amounts, or precise financial figures, replacing them with friendly, general
                terms (e.g. "your recent home purchase").
              </p>
            </div>

            <div className="space-y-1">
              <span className="text-base font-bold text-slate-800 flex items-center gap-1">
                <ShieldCheck className="size-4 text-emerald-500 shrink-0" />
                Is customer and user data safe?
              </span>
              <p>
                Yes, absolutely. We utilize secure, enterprise-grade Gemini models. Most importantly,{" "}
                <strong>none of your data or your clients' information is ever used to train public models</strong>.
                Your customer names, loans, and feedback remain completely private and secure.
              </p>
            </div>

            <div className="space-y-1">
              <span className="text-base font-bold text-slate-800 flex items-center gap-1">
                <ShieldCheck className="size-4 text-emerald-500 shrink-0" />
                What languages are supported by the translation engine?
              </span>
              <p>
                Currently, we support full localization in <strong>English</strong> and <strong>Spanish</strong>. If you
                are interested in support for other languages, please contact us at{" "}
                <a href="mailto:support@socialcoach.io" className="text-primary hover:underline font-semibold">
                  support@socialcoach.io
                </a>{" "}
                and let us know!
              </p>
            </div>
          </div>
        </div>
      </Dialog.Content>

      <Dialog.Footer>
        <Button onClick={() => onOpenChange(false)} type="button">
          Close Help
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
}
